<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      アカウント認証
    </Header>
    <div class="mt-4 mb-5 mx-4">
      <CustomValidationObserver
        ref="form"
        v-slot="{ handleSubmit }"
        tag="div"
        class="w-100"
      >
        <BForm>
          <ValidationProvider
            vid="phoneNumber"
            v-slot="validationState"
            name="電話番号"
            rules="required"
            :value="phoneNumberWithHyphen"
          >
            <TextFieldGroup
              :value="phoneNumberWithHyphen"
              label="設定した電話番号"
              disabled
              class="mb-3"
              :validation-state="validationState"
            />
          </ValidationProvider>

          <ValidationProvider
            vid="email"
            v-slot="validationState"
            name="メールアドレス"
            rules="required|email"
            v-model="email"
          >
            <TextFieldGroup
              v-model="email"
              label="メールアドレス"
              appLabelText="必須"
              class="mb-3"
              :validation-state="validationState"
            />
          </ValidationProvider>

          <BRow class="mx-n2">
            <BCol class="px-2">
              <ValidationProvider
                v-slot="validationState"
                name="名前（姓）"
                vid="lastName"
                rules="required"
                v-model="lastName"
              >
                <TextFieldGroup
                  v-model="lastName"
                  label="名前"
                  appLabelText="必須"
                  placeholder="介助"
                  type="text"
                  class="mb-3"
                  :validation-state="validationState"
                />
              </ValidationProvider>
            </BCol>
            <BCol class="px-2">
              <ValidationProvider
                v-slot="validationState"
                name="名前（名）"
                vid="firstName"
                rules="required"
                v-model="firstName"
              >
                <TextFieldGroup
                  v-model="firstName"
                  type="text"
                  placeholder="太郎"
                  class="mb-3 mt-4 pt-2"
                  :validation-state="validationState"
                />
              </ValidationProvider>
            </BCol>
          </BRow>
          <BRow class="mx-n2">
            <BCol class="px-2">
              <ValidationProvider
                v-slot="validationState"
                name="フリガナ(カタカナ)(姓)"
                vid="lastNameKana"
                rules="required|hankaku_kana"
                v-model="lastNameKana"
              >
                <TextFieldGroup
                  v-model="lastNameKana"
                  label="フリガナ"
                  appLabelText="必須"
                  placeholder="カイテク"
                  type="text"
                  class="mb-3"
                  :validation-state="validationState"
                  @blur="() => onBlurLastNameKana(validationState.handleChange)"
                />
              </ValidationProvider>
            </BCol>
            <BCol class="px-2">
              <ValidationProvider
                v-slot="validationState"
                name="フリガナ(カタカナ)(名)"
                vid="firstNameKana"
                rules="required|hankaku_kana"
                v-model="firstNameKana"
              >
                <TextFieldGroup
                  v-model="firstNameKana"
                  type="text"
                  placeholder="タロウ"
                  class="mb-3 mt-4 pt-2"
                  :validation-state="validationState"
                  @blur="
                    () => onBlurFirstNameKana(validationState.handleChange)
                  "
                />
              </ValidationProvider>
            </BCol>
          </BRow>

          <ValidationProvider
            vid="birthDate"
            v-slot="validationState"
            name="生年月日"
            rules="required"
            v-model="birthDate"
          >
            <TextFieldGroup
              v-model="birthDate"
              type="date"
              label="生年月日"
              appLabelText="必須"
              class="mb-4"
              :validation-state="validationState"
            />
          </ValidationProvider>

          <AppButton block @click="handleSubmit(onClickAuth)">
            認証する
          </AppButton>
        </BForm>
      </CustomValidationObserver>
    </div>
    <BlockBack />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import TextFieldGroup from "@/components/molecules/form/TextFieldGroup.vue";
import Header from "@/components/organisms/common/Header.vue";
import { authStore } from "@/store/auth";
import { authService } from "@/app/services/AuthService";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { fullSizeToHalfSize } from "@/utils/fullSizeToHalfSize";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import ValidationObserver from "@/components/organisms/validation/ValidationObserver.vue";
import { HandleChangeType } from "@/types/Validation";

@Component({
  components: {
    AppButton,
    FeatherIcon,
    Header,
    TextFieldGroup,
    BlockBack
  }
})
export default class V1UserAccountAuth extends Vue {
  lastName = "";
  firstName = "";
  lastNameKana = "";
  firstNameKana = "";
  email = "";
  birthDate = "1990-01-01";

  get phoneNumber() {
    return authStore.temporaryPhoneNumber;
  }

  get serviceName() {
    return authStore.workerService.serviceName;
  }

  get phoneNumberWithHyphen() {
    return formatPhoneNumber(this.phoneNumber);
  }

  async onClickAuth() {
    try {
      this.$loading.show();
      await this.authenticateV1User();
      this.$router.push({ name: "V1UserAuthCompleted" });
      return;
    } catch (e) {
      const error = e as ErrorType;
      if (!error || error instanceof Error) {
        showErrorModal(this, error);
        return;
      }
      // phoneNumberもしくはserviceNameが不正な場合
      if (!error.response || !error.response.data) {
        await showErrorModal(this, new Error("不正な操作です"));
        this.$router.push({ name: "Welcome" });
        return;
      }

      // 入力情報にエラーがある場合
      // 前処理の showErrorModal がコールされるため、現状ここにたどり着くケースはない
      if (
        error.response.data.email ||
        error.response.data.firstName ||
        error.response.data.lastName ||
        error.response.data.firstNameKana ||
        error.response.data.lastNameKana ||
        error.response.data.birthDate
      ) {
        (this.$refs.form as typeof ValidationObserver).setErrors(
          error.response.data
        );
        return;
      }

      // その他エラーの場合
      showErrorModal(this, error);
      return;
    } finally {
      this.$loading.hide();
    }
  }
  authenticateV1User() {
    if (!this.phoneNumber || !this.serviceName) throw new Error();
    const body = {
      email: this.email,
      phoneNumber: this.phoneNumber,
      firstName: this.firstName,
      lastName: this.lastName,
      firstNameKana: this.firstNameKana,
      lastNameKana: this.lastNameKana,
      birthDate: this.birthDate,
      serviceName: this.serviceName
    };
    return authService.authenticateV1User(body);
  }
  onBlurFirstNameKana(handleChange: HandleChangeType) {
    this.firstNameKana = fullSizeToHalfSize(this.firstNameKana);
    handleChange(this.firstNameKana, true);
  }
  onBlurLastNameKana(handleChange: HandleChangeType) {
    this.lastNameKana = fullSizeToHalfSize(this.lastNameKana);
    handleChange(this.lastNameKana, true);
  }
}
</script>
